import React, { ImgHTMLAttributes, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Loader } from 'ncoded-component-library';
import logo from 'assets/images/logo.webp';
import noImgAvatar from 'assets/images/no-img-avatar.webp';
import LazyLoad from 'react-lazy-load';
import utils from '../../utils';
import { Size } from 'models/Size';

import './ResolveImage.styles.scss';

type ResolveImageProps = {
  className?: string;
  rootProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
  asAvatar?: boolean;
  asCompanyLogo?: boolean;
  asIllustration?: boolean;
  loaderSize?: Size;
  shouldLazyLoad?: boolean;
  getImageSize?: (naturalWidth: number, naturalHeight: number) => void;
} & ImgHTMLAttributes<HTMLImageElement>;

const ResolveImage: React.FC<ResolveImageProps> = (props) => {
  const {
    className,
    rootProps,
    asAvatar,
    asCompanyLogo,
    asIllustration,
    loaderSize,
    src = asAvatar ? noImgAvatar : logo,
    shouldLazyLoad = true,
    getImageSize,
    ...rest
  } = props;
  const [isLoaded, setIsLoaded] = useState(false);

  const classes = classNames('resolve-image', className, {
    avatar: asAvatar,
    company: asCompanyLogo,
    illustration: asIllustration,
  });

  const ImageElement = useMemo(
    () => (
      <img
        src={src}
        alt={rest.alt || rest.title}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = logo;
        }}
        onLoad={(e) => {
          const { naturalWidth, naturalHeight } = e.currentTarget;
          getImageSize?.(naturalWidth, naturalHeight);
          setIsLoaded(true);
        }}
        {...rest}
      />
    ),
    [rest, src, getImageSize],
  );

  // In case pre-rendering for SEO optimization
  if (utils.isSSGBot()) {
    return (
      <div className={classes} {...rootProps}>
        <div className="LazyLoad is-visible">{ImageElement}</div>
      </div>
    );
  }

  return (
    <div className={classes} {...rootProps}>
      {!isLoaded && <Loader size={loaderSize} />}

      {shouldLazyLoad ? (
        <LazyLoad>{ImageElement}</LazyLoad>
      ) : (
        <div className="not-lazy-load">{ImageElement}</div>
      )}
    </div>
  );
};

export default ResolveImage;
